import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  FORMES_EMBOUT, OBTURATEUR_TYPES, MONTAGE_LABELS, materialLabel
} from './orderFormConstants';
import FormEmboutDetails from './FormEmboutDetails';
import FormObturateurSection from './FormObturateurSection';
import FormFormeMatiere from './FormFormeMatiere';
import FormConformateurSection from './FormConformateurSection';
import { CaretDown } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AccordionSection({ title, defaultOpen = false, children, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden" data-testid={testId}>
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 sm:p-5 text-left hover:bg-zinc-50 transition-colors"
        data-testid={`${testId}-toggle`}>
        <span className="font-heading font-semibold text-sm sm:text-base">{title}</span>
        <CaretDown size={18} className={`text-zinc-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-4 sm:px-5 pb-4 sm:pb-5 border-t border-zinc-100">{children}</div>}
    </div>
  );
}

// Auto vent based on forme
const VENT_MAP = {
  canule: '2mm',
  microcanule: '2.5mm',
  canule_epaulement: '1.5mm',
  canule_demi_epaulement: '1.5mm',
  demi_epaulement: '1.5mm',
  squelette: '1.5mm',
  pince_crabe: '1.5mm',
  fond_conque: '1mm',
  demi_conque: '1mm',
};

export default function OrderFormFields({ form, compact = false, idPrefix = '', changedFields = null, user = null }) {
  const id = (name) => idPrefix ? `${idPrefix}-${name}` : name;
  const labelClass = compact ? 'label-overline mb-1 block' : 'label-overline mb-2 block';
  const gapClass = compact ? 'gap-3' : 'gap-4';
  const hasDefaultMarquage = !!(user && user.default_marquage_image_id);

  const isRIC = form.emboutType === 'ecouteur_deporte';
  const isAntiBruitF = form.obturateurType === 'anti_bruit_filtre';
  const isBouchonMusique = form.obturateurType === 'bouchon_musique';
  const isFilteredType = isAntiBruitF || isBouchonMusique;
  const isAntiEau = form.obturateurType === 'anti_eau';

  // Auto-set vent when forme changes
  const handleFormeChange = (val) => {
    form.setFormeEmbout(val);
    const autoVent = VENT_MAP[val] || 'Sans';
    form.setEventOD(autoVent);
    form.setEventOG(autoVent);
  };

  if (compact) {
    // Keep compact mode as-is for ManualOrderDialog
    return <CompactFields form={form} idPrefix={idPrefix} changedFields={changedFields} />;
  }

  return (
    <div className="space-y-3">
      {/* Patient — open by default */}
      <AccordionSection title="Patient" defaultOpen={true} testId="section-patient">
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 mb-4" data-testid="rgpd-patient-warning">
          <p className="text-xs sm:text-sm text-amber-800 font-medium">Merci de ne pas renseigner de donnees nominatives patient. Utilisez une reference non nominative.</p>
        </div>
        <div className={`grid grid-cols-1 sm:grid-cols-2 ${gapClass}`}>
          <div>
            <Label className={labelClass}>Reference patient (non nominatif) *</Label>
            <Input placeholder="Ex: REF-2026-001" value={form.patientRef} onChange={(e) => form.setPatientRef(e.target.value)} className="rounded-sm" required data-testid="patient-ref-input" />
          </div>
          <div>
            <Label className={labelClass}>Date de livraison souhaitee</Label>
            <Input type="date" value={form.deliveryDate} onChange={(e) => form.setDeliveryDate(e.target.value)} className="rounded-sm" data-testid="delivery-date-input" />
          </div>
          <div>
            <Label className={labelClass}>Type de patient</Label>
            <RadioGroup value={form.patientType} onValueChange={form.setPatientType} className="flex flex-wrap gap-3">
              <div className="flex items-center space-x-2"><RadioGroupItem value="nourrisson" id={id('nourrisson')} /><Label htmlFor={id('nourrisson')} className="cursor-pointer text-sm">Nourrisson</Label></div>
              <div className="flex items-center space-x-2"><RadioGroupItem value="enfant" id={id('enfant')} /><Label htmlFor={id('enfant')} className="cursor-pointer text-sm">Enfant</Label></div>
              <div className="flex items-center space-x-2"><RadioGroupItem value="adulte" id={id('adulte')} /><Label htmlFor={id('adulte')} className="cursor-pointer text-sm">Adulte</Label></div>
            </RadioGroup>
          </div>
          <div>
            <Label className={labelClass}>Texture oreille</Label>
            <Select value={form.textureOreille} onValueChange={form.setTextureOreille}>
              <SelectTrigger className="rounded-sm bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="souple">Souple</SelectItem>
                <SelectItem value="rigide">Rigide</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </AccordionSection>

      {/* Type de fabrication — includes ear side */}
      <AccordionSection title="Type de fabrication" defaultOpen={false} testId="section-fabrication">
        <div className="space-y-4 pt-2">
          {/* Ear side */}
          <div>
            <Label className={labelClass}>Oreille *</Label>
            <RadioGroup value={form.earSide} onValueChange={form.setEarSide} className="flex flex-col sm:flex-row gap-3">
              <div className="flex items-center space-x-2"><RadioGroupItem value="right" id={id('od')} data-testid="ear-right-radio" /><Label htmlFor={id('od')} className="cursor-pointer font-medium">Oreille droite</Label></div>
              <div className="flex items-center space-x-2"><RadioGroupItem value="left" id={id('og')} data-testid="ear-left-radio" /><Label htmlFor={id('og')} className="cursor-pointer font-medium">Oreille gauche</Label></div>
              <div className="flex items-center space-x-2"><RadioGroupItem value="both" id={id('bilateral')} data-testid="ear-both-radio" /><Label htmlFor={id('bilateral')} className="cursor-pointer font-medium">Bilateral</Label></div>
            </RadioGroup>
          </div>

          {/* Bilateral: different forms checkbox */}
          {form.isBilateral && form.emboutType !== 'silk' && (
            <div className="bg-zinc-50 border border-zinc-200 p-3 rounded-sm">
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox checked={form.perEarDiff} onCheckedChange={form.setPerEarDiff} data-testid="per-ear-diff-checkbox" />
                <span className="text-sm font-medium">Forme differente entre droite et gauche</span>
              </label>
            </div>
          )}

          {/* Fabrication type + details */}
          <FormEmboutDetails form={form} compact={false} idPrefix={idPrefix} changedFields={changedFields} />
        </div>
      </AccordionSection>

      {/* Obturateur options */}
      <FormObturateurSection form={form} compact={false} idPrefix={idPrefix} />

      {/* Conformateur d'oreille — section dédiée (silicone transparent, description, photos) */}
      {form.fabricationType === 'conformateur_oreille' && (
        <FormConformateurSection form={form} compact={false} />
      )}

      {/* Forme et matiere (hidden for obturateur/conformateur — handled par leurs sections dédiées) */}
      {form.fabricationType === 'embout_auditif' && (
        <AccordionSection title="Forme et matiere" defaultOpen={false} testId="section-forme">
          <div className="pt-2">
            <FormFormeMatiere form={form} compact={false} changedFields={changedFields} onFormeChange={handleFormeChange} />
          </div>
        </AccordionSection>
      )}

      {/* Options supplementaires (hidden for Intégral, BTE et Silk — montage handled in dedicated section / tarif inclus) */}
      {form.fabricationType === 'embout_auditif' && form.emboutType !== 'integral' && form.emboutType !== 'bte' && form.emboutType !== 'silk' && form.availableOptions.length > 0 && (
        <AccordionSection title="Options supplementaires" defaultOpen={false} testId="section-options">
          <div className="pt-2">
            <div className="space-y-2">
              {form.availableOptions.map(opt => (
                <label key={opt.lookup_key}
                  className={`flex items-center justify-between p-2.5 rounded-sm border cursor-pointer transition-colors ${form.selectedOptions.includes(opt.lookup_key) ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`}
                  data-testid={`option-${opt.lookup_key}`}>
                  <div className="flex items-center gap-3">
                    <Checkbox checked={form.selectedOptions.includes(opt.lookup_key)} onCheckedChange={() => form.toggleOption(opt.lookup_key)} />
                    <span className="font-medium text-sm">{opt.name}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </AccordionSection>
      )}

      {/* Marquage special */}
      {form.fabricationType === 'embout_auditif' && !form.isResineSouple && (
        <AccordionSection title="Marquage special" defaultOpen={false} testId="section-marquage">
          <div className="pt-2 space-y-3">
            {hasDefaultMarquage && (
              <div className="p-3 bg-emerald-50 border border-emerald-300 rounded-sm text-xs text-emerald-900" data-testid="default-marquage-info">
                <strong>Marquage par défaut actif</strong> sur votre compte : il sera appliqué automatiquement à toutes vos commandes sans facturation supplémentaire.
                {form.marquageSpecial && (
                  <div className="mt-1 text-amber-900 bg-amber-50 border border-amber-300 rounded-sm p-2">
                    ⚠️ Attention : le marquage spécial que vous saisirez ci-dessous <strong>écrasera totalement</strong> le marquage par défaut pour cette commande uniquement.
                  </div>
                )}
              </div>
            )}
            <label className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${form.marquageSpecial ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`} data-testid="marquage-special-checkbox-embout">
              <Checkbox checked={form.marquageSpecial} onCheckedChange={(v) => {
                form.setMarquageSpecial(v);
                if (!v) {
                  form.setMarquageSpecialTexte('');
                  form.setMarquageSpecialImageId('');
                  form.setMarquageSpecialMode('texte');
                }
              }} />
              <div>
                <span className="font-medium text-sm">Marquage special</span>
                <p className="text-xs text-zinc-500 mt-0.5">Si vous souhaitez faire un marquage special (texte ou image), merci de nous l&apos;indiquer en cliquant sur cette case.</p>
              </div>
            </label>
            {form.marquageSpecial && (
              <MarquageSpecialBlock form={form} id={id} />
            )}
          </div>
        </AccordionSection>
      )}

      {/* Commentaire */}
      <AccordionSection title="Commentaire" defaultOpen={false} testId="section-commentaire">
        <div className="pt-2">
          <Label className={labelClass}>Instructions complementaires</Label>
          <textarea placeholder="Toute information utile pour la fabrication..."
            value={form.notes} onChange={(e) => form.setNotes(e.target.value)}
            className="w-full p-3 border border-zinc-200 rounded-sm resize-none h-20 sm:h-24 focus:outline-none focus:ring-2 focus:ring-zinc-900 text-sm"
            data-testid="notes-textarea" />
        </div>
      </AccordionSection>

      {/* Recapitulatif */}
      {form.resolvedProduct && (
        <div className="bg-zinc-900 text-white p-4 sm:p-6 rounded-sm" data-testid="order-summary">
          <h2 className="font-heading font-semibold text-base sm:text-lg mb-3">Recapitulatif</h2>
          {form.deliveryDate && (
            <div className="bg-red-600/20 border border-red-500 rounded-sm p-2.5 mb-3 flex items-center justify-between gap-2" data-testid="recap-delivery-date">
              <span className="text-xs uppercase tracking-wider text-red-200 font-semibold">Livraison souhaitée</span>
              <span className="text-base font-bold text-red-300">{new Date(form.deliveryDate).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}</span>
            </div>
          )}
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-zinc-400">Type</span><span>{
              form.fabricationType === 'obturateur' ? 'Obturateur' :
              form.fabricationType === 'conformateur_oreille' ? "Conformateur d'oreille" :
              'Embout auditif'
            }</span></div>
            <div className="flex justify-between"><span className="text-zinc-400">Produit</span><span>{form.resolvedProduct.name}</span></div>
            {form.fabricationType === 'obturateur' && form.obturateurType && (
              <>
                <div className="flex justify-between"><span className="text-zinc-400">Sous-type</span><span>{OBTURATEUR_TYPES.find(t => t.value === form.obturateurType)?.label}</span></div>
                {isFilteredType && form.obturateurAttenuation && (
                  <div className="flex justify-between"><span className="text-zinc-400">Attenuation</span><span>{form.obturateurAttenuation}</span></div>
                )}
                {(isAntiEau || isAntiBruitF || isBouchonMusique) && (
                  <div className="flex justify-between"><span className="text-zinc-400">Poignee</span><span>{form.obturateurPoignee ? 'Oui' : 'Non'}</span></div>
                )}
                <div className="flex justify-between"><span className="text-zinc-400">Cordon</span><span>{form.obturateurCordon ? 'Oui' : 'Non'}</span></div>
                <div className="flex justify-between"><span className="text-zinc-400">Etui</span><span>{form.obturateurEtui ? 'Oui' : 'Non'}</span></div>
              </>
            )}
            {form.fabricationType === 'embout_auditif' && form.formeEmbout && (
              <>
                {(() => {
                  const buildLabel = (formeVal, demi, evidee) => {
                    if (formeVal === 'silk') return 'Intra (Silk)';
                    let label = FORMES_EMBOUT.find(f => f.value === formeVal)?.label || formeVal;
                    const mods = [];
                    if (demi && formeVal === 'canule_epaulement') mods.push('Demi-épaulement');
                    if (evidee) mods.push('Évidée');
                    return mods.length ? `${label} + ${mods.join(' + ')}` : label;
                  };
                  return form.isBilateral && form.perEarDiff ? (
                    <>
                      <div className="flex justify-between"><span className="text-zinc-400">Forme OD</span><span>{buildLabel(form.formeEmbout, form.formeDemiEpaulement, form.formeEvidee)}</span></div>
                      <div className="flex justify-between"><span className="text-zinc-400">Forme OG</span><span>{form.formeEmboutOG ? buildLabel(form.formeEmboutOG, form.formeDemiEpaulementOG, form.formeEvideeOG) : '—'}</span></div>
                    </>
                  ) : (
                    <div className="flex justify-between"><span className="text-zinc-400">Forme</span><span>{buildLabel(form.formeEmbout, form.formeDemiEpaulement, form.formeEvidee)}</span></div>
                  );
                })()}
              </>
            )}
            {form.fabricationType === 'conformateur_oreille' && (
              <>
                <div className="flex justify-between"><span className="text-zinc-400">Matière</span><span>Silicone transparent</span></div>
                <div className="flex justify-between"><span className="text-zinc-400">Dureté</span><span>{form.durete} SH</span></div>
                {(form.conformateurPhotoIds || []).length > 0 && (
                  <div className="flex justify-between"><span className="text-zinc-400">Photos</span><span>{form.conformateurPhotoIds.length} jointe(s)</span></div>
                )}
              </>
            )}
            {form.fabricationType !== 'conformateur_oreille' && form.isBilateral && form.perEarDiff ? (
              <>
                <div className="flex justify-between"><span className="text-zinc-400">Matiere OD</span><span>{materialLabel(form.matiere)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-400">Matiere OG</span><span>{materialLabel(form.matiereOG)}</span></div>
              </>
            ) : form.fabricationType !== 'conformateur_oreille' ? (
              <div className="flex justify-between"><span className="text-zinc-400">Matiere</span><span>{materialLabel(form.matiere)}</span></div>
            ) : null}
            {form.montageType && (
              <div className="flex justify-between"><span className="text-zinc-400">Montage</span><span>{MONTAGE_LABELS[form.montageType] || form.montageType}</span></div>
            )}
            {/* Filtre choice (SlimTip Link / Intégral) */}
            {((form.emboutType === 'ecouteur_deporte' && form.montageType === 'slim_tip_link') || form.emboutType === 'integral') && form.filtreType && (
              <div className="flex justify-between"><span className="text-zinc-400">Filtre</span><span>{form.filtreType === 'hf4' ? 'HF4' : 'CeruStop'}</span></div>
            )}
            {/* BTE brand-specific mini-tube */}
            {form.emboutType === 'bte' && form.bteBrandSpecificEnabled && form.bteBrandSpecificType && (
              <div className="flex justify-between"><span className="text-zinc-400">Mini-tube marque</span><span>
                {form.bteBrandSpecificType === 'power_slim_tube' ? 'Power SlimTube' : `Corda MiniFit${form.bteCordaMinifitDiameter ? ` ${form.bteCordaMinifitDiameter} mm` : ''}`}
              </span></div>
            )}
            {form.selectedOptionProducts.length > 0 && (
              <div className="flex justify-between"><span className="text-zinc-400">Options</span><span className="text-right">{form.selectedOptionProducts.map(o => o.name).join(', ')}</span></div>
            )}
            {form.finitionAnallergique && (
              <div className="flex justify-between"><span className="text-zinc-400">Anallergique</span><span>Oui</span></div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Compact mode for ManualOrderDialog (keeps original flat layout)
function CompactFields({ form, idPrefix, changedFields }) {
  const id = (name) => idPrefix ? `${idPrefix}-${name}` : name;
  const sectionClass = 'bg-white border border-zinc-200 p-3 sm:p-4 rounded-sm';
  const headingClass = 'font-heading font-semibold text-sm mb-3 pb-2 border-b border-zinc-200';
  const labelClass = 'label-overline mb-1 block';

  return (
    <div className="space-y-4 sm:space-y-5">
      {/* Patient */}
      <div className={sectionClass}>
        <h3 className={headingClass}>Patient</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className={labelClass}>Reference patient *</Label>
            <Input placeholder="Ex: REF-2026-001" value={form.patientRef} onChange={(e) => form.setPatientRef(e.target.value)} className="rounded-sm" required data-testid="patient-ref-input" />
          </div>
          <div>
            <Label className={labelClass}>Oreille *</Label>
            <RadioGroup value={form.earSide} onValueChange={form.setEarSide} className="flex gap-3">
              <div className="flex items-center space-x-2"><RadioGroupItem value="right" id={id('od')} /><Label htmlFor={id('od')} className="cursor-pointer text-sm">OD</Label></div>
              <div className="flex items-center space-x-2"><RadioGroupItem value="left" id={id('og')} /><Label htmlFor={id('og')} className="cursor-pointer text-sm">OG</Label></div>
              <div className="flex items-center space-x-2"><RadioGroupItem value="both" id={id('bilateral')} /><Label htmlFor={id('bilateral')} className="cursor-pointer text-sm">Bi.</Label></div>
            </RadioGroup>
          </div>
        </div>
      </div>

      <FormEmboutDetails form={form} compact={true} idPrefix={idPrefix} changedFields={changedFields} />
      <FormObturateurSection form={form} compact={true} idPrefix={idPrefix} />
      <FormConformateurSection form={form} compact={true} />
      <FormFormeMatiere form={form} compact={true} changedFields={changedFields} />

      {/* Notes */}
      <div>
        <Label className={labelClass}>Notes / Instructions</Label>
        <textarea placeholder="Instructions particulieres..." value={form.notes}
          onChange={(e) => form.setNotes(e.target.value)}
          className="w-full p-3 border border-zinc-200 rounded-sm resize-none h-20 focus:outline-none focus:ring-2 focus:ring-zinc-900 text-sm"
          data-testid="notes-textarea" />
      </div>
    </div>
  );
}


function MarquageSpecialBlock({ form, id }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState('');

  // Recharger la preview quand un file_id existe déjà (édition de commande)
  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (form.marquageSpecialMode !== 'image' || !form.marquageSpecialImageId) {
        setPreview('');
        return;
      }
      try {
        const res = await axios.get(`${API}/orders/marquage-image/${form.marquageSpecialImageId}`, { responseType: 'blob' });
        if (alive) setPreview(URL.createObjectURL(res.data));
      } catch {
        if (alive) setPreview('');
      }
    };
    load();
    return () => { alive = false; };
  }, [form.marquageSpecialMode, form.marquageSpecialImageId]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.png')) {
      toast.error('Seuls les fichiers PNG sont acceptés');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 5 Mo)');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/orders/upload-marquage-image`, fd);
      form.setMarquageSpecialImageId(res.data.file_id);
      toast.success('Logo enregistré');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur upload logo');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="marquage-special-block">
      <RadioGroup
        value={form.marquageSpecialMode}
        onValueChange={(v) => {
          form.setMarquageSpecialMode(v);
          if (v === 'texte') form.setMarquageSpecialImageId('');
          if (v === 'image') form.setMarquageSpecialTexte('');
        }}
        className="flex gap-2"
      >
        <label className={`flex items-center gap-2 p-2.5 px-3 rounded-sm border cursor-pointer transition-colors bg-white ${form.marquageSpecialMode === 'texte' ? 'border-zinc-900' : 'border-zinc-200 hover:border-zinc-400'}`}>
          <RadioGroupItem value="texte" data-testid="marquage-mode-texte" />
          <span className="font-medium text-sm">Texte</span>
        </label>
        <label className={`flex items-center gap-2 p-2.5 px-3 rounded-sm border cursor-pointer transition-colors bg-white ${form.marquageSpecialMode === 'image' ? 'border-zinc-900' : 'border-zinc-200 hover:border-zinc-400'}`}>
          <RadioGroupItem value="image" data-testid="marquage-mode-image" />
          <span className="font-medium text-sm">Image (logo PNG)</span>
        </label>
      </RadioGroup>

      {form.marquageSpecialMode === 'texte' && (
        <Input
          placeholder="10 caractères max."
          value={form.marquageSpecialTexte}
          onChange={(e) => form.setMarquageSpecialTexte(e.target.value.slice(0, 10))}
          maxLength={10}
          className="rounded-sm"
          data-testid="marquage-special-input-embout"
        />
      )}

      {form.marquageSpecialMode === 'image' && (
        <div className="space-y-2">
          <p className="text-xs text-zinc-600">
            PNG uniquement, 5 Mo max. Le logo sera automatiquement redimensionné et recoloré (rouge/bleu) pour l&apos;impression UV. Il ne dépassera jamais la largeur du numéro de série.
          </p>
          <input
            type="file"
            accept="image/png,.png"
            onChange={handleUpload}
            disabled={uploading}
            className="block w-full text-sm text-zinc-700 file:mr-3 file:py-2 file:px-3 file:rounded-sm file:border-0 file:bg-zinc-900 file:text-white file:cursor-pointer hover:file:bg-zinc-800 disabled:opacity-50"
            data-testid={id('marquage-image-input')}
          />
          {uploading && <p className="text-xs text-zinc-500">Upload en cours...</p>}
          {preview && (
            <div className="p-3 bg-zinc-100 border border-zinc-200 rounded-sm inline-block" data-testid="marquage-image-preview">
              <p className="text-xs text-zinc-600 mb-2">Aperçu (fond blanc pour visibilité) :</p>
              <img src={preview} alt="Logo marquage" className="max-h-24 bg-white p-2 rounded" />
              <button
                type="button"
                onClick={() => { form.setMarquageSpecialImageId(''); setPreview(''); }}
                className="block mt-2 text-xs text-red-700 hover:underline"
                data-testid="marquage-image-remove"
              >
                Retirer le logo
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
